import React, { useState, useEffect, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  LinearProgress,
  TableSortLabel,
} from '@mui/material';
import {
  Dashboard,
  Edit,
  Delete,
  Visibility,
  Schedule,
  CheckCircle,
  Warning,
  Search,
  Inventory2,
} from '@mui/icons-material';
import { DraftBundle } from '../../types';
import { WarehouseAPI } from '../../services/api';
import PageHeader from '../common/PageHeader';
import StatPill from '../common/StatPill';
import SegmentedFilter from '../common/SegmentedFilter';
import ListRowCard from '../common/ListRowCard';
import PageContainer from '../common/PageContainer';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../common/useUrlParam';
import { useToast } from '../common/useToast';
import { eyebrow } from '../actionables/brand';
import { formatDate as formatDateUtil } from '../../utils/formatDate';
import {
  getTotalPickedItems as computeTotalPicked,
  getTotalRequiredItems as computeTotalRequired,
  compareDrafts,
  matchesDraftFilter,
} from '../../utils/draftBundleSort';

import { useNavigate } from 'react-router-dom';

// Status-driven accent shared by the left strip, status pill, progress bar, and percentage text
// so one draft always reads a single colour. Mirrors the theme success/warning tokens.
type StatusAccent = { main: string; soft: string; label: string };
const statusAccent = (status: string): StatusAccent => {
  switch (status) {
    case 'picked':
      return { main: '#16A34A', soft: 'rgba(22,163,74,0.12)', label: 'All Picked' };
    case 'partially_picked':
      return { main: '#D97706', soft: 'rgba(217,119,6,0.12)', label: 'Partially Picked' };
    default:
      return { main: '#6B625B', soft: 'rgba(107,98,91,0.12)', label: 'Unknown' };
  }
};

// Replicated from Header.tsx (layout/Header.tsx) so owner avatars harmonise with the Salty theme.
// Header's gradient is an inline useMemo keyed on the authed user, not an exported helper, so per
// the plan we replicate the curated palette + hash locally rather than refactor Header. Seeded by
// the draft OWNER's identity (not the viewer) so each owner gets a consistent, distinct colour.
const AVATAR_PALETTE: ReadonlyArray<readonly [string, string]> = [
  ['#DC5829', '#B8451E'], // Salty orange
  ['#C2410C', '#9A3412'], // burnt orange
  ['#B45309', '#92400E'], // amber/ochre
  ['#A16207', '#854D0E'], // mustard
  ['#9D5C3F', '#7C4A32'], // terracotta/clay
  ['#6B625B', '#4A433D'], // warm stone (neutral)
  ['#8A5A44', '#6E4633'], // cocoa
];
const ownerGradient = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const [from, to] = AVATAR_PALETTE[h % AVATAR_PALETTE.length];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
};

type StatusFilter = 'all' | 'picked' | 'partially_picked';

// Sortable columns for the draft list. Default is `updated`/`desc`, which preserves the current
// "most recent first" ordering from loadDraftsFromStorage.
type SortKey = 'name' | 'status' | 'progress' | 'updated';
type SortDir = 'asc' | 'desc';

// Fixed column tracks shared by the list header row AND every data row, so Bundle / [Owner] /
// Status / Progress / Actions line up in fixed vertical columns (exactly like InventoryPage's
// ROW_GRID). The Owner track is included only in the admin cross-user view (showOwner); on xs the
// layout collapses to a compact 2-column stack like Inventory.
const rowGrid = (showOwner: boolean) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr auto',
    md: showOwner
      ? 'minmax(220px, 2fr) minmax(180px, 1.2fr) 150px minmax(170px, 1fr) auto'
      : 'minmax(220px, 2fr) 150px minmax(170px, 1fr) auto',
  },
  alignItems: 'center',
  columnGap: 2,
} as const);

const DraftBundleDashboard: React.FC = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const toast = useToast();
  const [drafts, setDrafts] = useState<DraftBundle[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<DraftBundle | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Filter/search/sort/page live in the URL (prefix "dr") so a refresh restores exactly where the
  // user left off. Defaults stay OUT of the URL; changing a filter/search/sort resets the page in
  // the SAME update (via useSetUrlParams). The loader, mapping, and lastUpdated sort are untouched,
  // and the rendered list is a derived memo never written back.
  const setUrlParams = useSetUrlParams();
  const [searchQuery] = useUrlParam('drq', '');
  const [statusFilterParam] = useUrlParam('drstatus', 'all');
  const statusFilter = statusFilterParam as StatusFilter;
  const [sortKeyParam] = useUrlParam('drsort', 'updated');
  const [sortDirParam] = useUrlParam('drdir', 'desc');
  const sortKey = sortKeyParam as SortKey;
  const sortDir = sortDirParam as SortDir;
  const [pageParam, setPageParam] = useUrlParam('drpage', '0');
  const page = parseInt(pageParam, 10) || 0;
  const setPage = (v: number) => setPageParam(v === 0 ? '0' : String(v));

  // Changing search resets the page in the same URL update.
  const setSearchQuery = (v: string) => setUrlParams({ drq: v === '' ? null : v, drpage: null });
  const setStatusFilter = (v: StatusFilter) => setUrlParams({ drstatus: v === 'all' ? null : v, drpage: null });

  // Load drafts from localStorage on component mount
  useEffect(() => {
    loadDraftsFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDraftsFromStorage = async () => {
    try {
      const backendDrafts = await WarehouseAPI.getDrafts();

      const formattedDrafts: DraftBundle[] = backendDrafts.map(bd => {
        if (bd.metadata) {
          return {
            ...bd.metadata,
            id: bd._id || bd.id,
            createdAt: bd.createdAt,
            lastUpdated: bd.updatedAt || bd.lastUpdated || bd.createdAt,
            owner: bd.owner,
            isOwner: bd.isOwner !== false,
          };
        }
        // Fallback
        return {
          id: bd._id || bd.id,
          bundleSKU: { id: '', name: bd.bundleSku, description: '', childSKUs: [] },
          quantity: bd.targetQuantity,
          childSKUPicking: {},
          createdAt: bd.createdAt,
          lastUpdated: bd.updatedAt || bd.createdAt,
          status: 'partially_picked',
          owner: bd.owner,
          isOwner: bd.isOwner !== false,
        };
      });

      // Sort by last updated (most recent first)
      formattedDrafts.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
      setDrafts(formattedDrafts);
    } catch (error) {
      console.error('Error loading drafts from backend:', error);
      toast.error('Error loading drafts from backend');
    }
  };

  const handleResumeDraft = (draft: DraftBundle) => {
    navigate(`/create-bundle?resume=${draft.id}`);
  };

  const handleDeleteDraft = (draft: DraftBundle) => {
    setSelectedDraft(draft);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (selectedDraft) {
      try {
        await WarehouseAPI.deleteDraft(selectedDraft.id);
        setDrafts(drafts.filter(d => d.id !== selectedDraft.id));
        setDeleteDialogOpen(false);
        setSelectedDraft(null);
      } catch (error) {
        console.error('Error deleting draft:', error);
        toast.error('Failed to delete draft');
      }
    }
  };

  const handleViewDetails = (draft: DraftBundle) => {
    setSelectedDraft(draft);
    setViewDialogOpen(true);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'picked':
        return <Chip label="All Picked" color="success" size="small" icon={<CheckCircle />} />;
      case 'partially_picked':
        return <Chip label="Partially Picked" color="warning" size="small" icon={<Warning />} />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  // Shared guarded en-GB formatter (was a different, unguarded toLocaleString()). Rewired to the
  // consolidated util so empty/invalid dates render an em dash consistently with the inventory
  // surfaces; valid dates keep the "DD Mon YYYY, hh:mm a" format.
  const formatDate = (dateString: string) => formatDateUtil(dateString);

  const getTotalPickedItems = (draft: DraftBundle): number => computeTotalPicked(draft);

  const getTotalRequiredItems = (draft: DraftBundle): number => computeTotalRequired(draft);

  // Admins also receive other users' drafts; those are view-only.
  const showOwner = drafts.some(d => d.isOwner === false);
  const ownerLabel = (d: DraftBundle) => d.owner?.name?.trim() || d.owner?.email || 'Unknown user';

  // Summary stats computed from the full loaded set (not the filtered view).
  const totalDrafts = drafts.length;
  const inProgressCount = drafts.filter(d => d.status === 'partially_picked').length;

  // Sort the full loaded set first (sort → filter → slice). Always sorts a COPY so the source
  // array is never mutated. The progress comparator reuses the same picked/required helpers the
  // rendered progress bar uses, so sort order and the displayed percentage stay consistent.
  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => compareDrafts(a, b, sortKey, sortDir));
  }, [drafts, sortKey, sortDir]);

  // Client-side filter over the sorted drafts. Search matches bundle name, and (only in admin
  // cross-user view) the creator name/email. Status filter narrows by draft.status. filteredDrafts
  // is the full filtered total shown in the "N drafts" count and passed to the pager.
  const filteredDrafts = useMemo(() => {
    return sortedDrafts.filter(d => matchesDraftFilter(d, searchQuery, statusFilter, showOwner));
  }, [sortedDrafts, searchQuery, statusFilter, showOwner]);

  // Page the filtered set client-side at DEFAULT_PAGE_SIZE (25).
  const pagedDrafts = useMemo(
    () => filteredDrafts.slice(page * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE),
    [filteredDrafts, page],
  );

  // Toggle direction when the same header is clicked; otherwise switch key with a sensible default
  // direction (desc for updated/progress, asc for name/status). Key + dir + the page reset all go
  // through one URL update so they never clobber each other.
  const handleSort = (key: SortKey) => {
    let nextKey = sortKey;
    let nextDir: SortDir;
    if (key === sortKey) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      nextKey = key;
      nextDir = key === 'updated' || key === 'progress' ? 'desc' : 'asc';
    }
    setUrlParams({
      drsort: nextKey === 'updated' ? null : nextKey,
      drdir: nextDir === 'desc' ? null : nextDir,
      drpage: null,
    });
  };

  const statusOptions: Array<{ value: StatusFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'picked', label: 'All Picked' },
    { value: 'partially_picked', label: 'Partially Picked' },
  ];

  const renderDraftCard = (draft: DraftBundle, index: number) => {
    const totalPicked = getTotalPickedItems(draft);
    const totalRequired = getTotalRequiredItems(draft);
    const progressPercentage = totalRequired > 0 ? (totalPicked / totalRequired) * 100 : 0;
    const accent = statusAccent(draft.status);
    const uniqueProducts = Object.keys(draft.childSKUPicking).length;
    const isAnotherUsers = draft.isOwner === false;

    return (
      <ListRowCard key={draft.id} accent={accent.main} index={index}>
        <Box sx={{ ...rowGrid(showOwner), pl: 3, pr: 2, py: 1.25, rowGap: 0.5 }}>
          {/* Bundle block */}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
              {draft.bundleSKU.name}
            </Typography>
            {draft.bundleSKU.description && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                {draft.bundleSKU.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Qty ${draft.quantity}`}
                size="small"
                sx={{ bgcolor: '#f4f1ea', color: '#1A1410', fontWeight: 700, borderRadius: '999px' }}
              />
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <Inventory2 sx={{ fontSize: 15 }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {uniqueProducts} unique {uniqueProducts === 1 ? 'product' : 'products'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Created By block (admin cross-user view only) */}
          {showOwner && (
            <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.25 }}>
              <Avatar
                sx={{
                  width: 36, height: 36, fontSize: '0.95rem', fontWeight: 800, color: '#fff',
                  background: ownerGradient(draft.owner?.name || draft.owner?.email || 'User'),
                  border: '2px solid #fff',
                  boxShadow: '0 0 0 1px rgba(26,20,15,0.08)',
                }}
              >
                {(isAnotherUsers ? ownerLabel(draft).charAt(0) : 'Y').toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                  {isAnotherUsers ? ownerLabel(draft) : 'You'}
                </Typography>
                {isAnotherUsers && draft.owner?.email && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
                    {draft.owner.email}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: '#8A8078', display: 'block' }}>
                  updated {formatDate(draft.lastUpdated)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Status pill */}
          <Box sx={{ justifySelf: 'start', display: { xs: 'none', md: 'block' } }}>
            {getStatusChip(draft.status)}
          </Box>

          {/* Progress */}
          <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {totalPicked}/{totalRequired} items
              </Typography>
              <Typography variant="caption" sx={{ color: accent.main, fontWeight: 800 }}>
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(progressPercentage, 100)}
              sx={{
                height: 7, borderRadius: '999px', bgcolor: '#F3F2EC',
                '& .MuiLinearProgress-bar': { bgcolor: accent.main, borderRadius: '999px' },
              }}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifySelf: 'end' }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => handleViewDetails(draft)}
                sx={{ color: '#DC5829', bgcolor: 'rgba(220,88,41,0.08)', '&:hover': { bgcolor: 'rgba(220,88,41,0.16)' } }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isAnotherUsers ? `Resume ${ownerLabel(draft)}'s draft` : 'Resume Draft'}>
              <IconButton
                size="small"
                onClick={() => handleResumeDraft(draft)}
                sx={{ color: '#DC5829', bgcolor: 'rgba(220,88,41,0.08)', '&:hover': { bgcolor: 'rgba(220,88,41,0.16)' } }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isAnotherUsers ? `Delete ${ownerLabel(draft)}'s draft` : 'Delete Draft'}>
              <IconButton
                size="small"
                onClick={() => handleDeleteDraft(draft)}
                sx={{ color: '#DC2626', bgcolor: 'rgba(220,38,38,0.08)', '&:hover': { bgcolor: 'rgba(220,38,38,0.16)' } }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </ListRowCard>
    );
  };

  return (
    <PageContainer>
      {/* Page header */}
      <PageHeader
        eyebrow="Bundles"
        icon={<Dashboard sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
        title="Draft Bundles"
        subtitle={showOwner
          ? 'Showing drafts from all users. As an admin you can resume, edit or delete any draft; other users only ever see their own.'
          : 'Manage your saved bundle drafts. Resume work on partially completed bundles or review completed pickings.'}
      />

      {drafts.length === 0 ? (
        // True-empty state (no drafts loaded at all).
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,88,41,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <Dashboard sx={{ color: '#DC5829' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>No Draft Bundles Found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Start creating a bundle and save it as a draft to see it here.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Toolbar: search + segmented status filter + summary stat pills */}
          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}
          >
            <TextField
              size="small"
              placeholder="Search by bundle name or creator…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 260, flex: '1 1 260px', '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#8A8078' }} />
                  </InputAdornment>
                ),
              }}
            />
            <SegmentedFilter options={statusOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as StatusFilter)} layoutId="draft-status-active" />
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <StatPill label="Total Drafts" value={totalDrafts} accent="#1A1410" />
              <StatPill label="In Progress" value={inProgressCount} accent="#D97706" />
            </Box>
          </Box>

          {filteredDrafts.length === 0 ? (
            // No-match state (drafts exist, but none match the current search/filter).
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', py: 7, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
                border: '1px dashed #ECECE4',
              }}
            >
              <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#f4f1ea', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <Search sx={{ color: '#8A8078' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>No matching drafts</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Try a different search term or clear the status filter to see all drafts.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* List title with the full filtered total (mirrors InventoryPage). */}
              <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.66rem', px: 3, pb: 0.5 }}>
                {filteredDrafts.length} {filteredDrafts.length === 1 ? 'draft' : 'drafts'}
              </Typography>
              {/* Column header row — shares rowGrid with the data rows so the sortable labels sit
                  exactly above their values. Hidden on xs where rows use the compact stacked
                  layout. */}
              <Box
                sx={{
                  ...rowGrid(showOwner),
                  display: { xs: 'none', md: 'grid' },
                  px: 3, pb: 0.5,
                }}
              >
                <Box
                  role="columnheader"
                  aria-sort={sortKey === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <TableSortLabel
                    active={sortKey === 'name'}
                    direction={sortKey === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                    sx={{ '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 } }}
                  >
                    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Bundle</Typography>
                  </TableSortLabel>
                </Box>
                {showOwner && (
                  <Box role="columnheader">
                    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Owner</Typography>
                  </Box>
                )}
                <Box
                  role="columnheader"
                  aria-sort={sortKey === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <TableSortLabel
                    active={sortKey === 'status'}
                    direction={sortKey === 'status' ? sortDir : 'asc'}
                    onClick={() => handleSort('status')}
                    sx={{ '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 } }}
                  >
                    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Status</Typography>
                  </TableSortLabel>
                </Box>
                <Box
                  role="columnheader"
                  aria-sort={sortKey === 'progress' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <TableSortLabel
                    active={sortKey === 'progress'}
                    direction={sortKey === 'progress' ? sortDir : 'asc'}
                    onClick={() => handleSort('progress')}
                    sx={{ '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 } }}
                  >
                    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Progress</Typography>
                  </TableSortLabel>
                </Box>
                <Box
                  role="columnheader"
                  aria-sort={sortKey === 'updated' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  sx={{ justifySelf: 'end' }}
                >
                  <TableSortLabel
                    active={sortKey === 'updated'}
                    direction={sortKey === 'updated' ? sortDir : 'asc'}
                    onClick={() => handleSort('updated')}
                    sx={{ '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 } }}
                  >
                    <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>Last Updated</Typography>
                  </TableSortLabel>
                </Box>
              </Box>
              {pagedDrafts.map((draft, i) => renderDraftCard(draft, i))}
              <TablePaginationBar
                page={page}
                total={filteredDrafts.length}
                rowsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
              />
            </Box>
          )}
        </>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Draft Bundle Details: {selectedDraft?.bundleSKU.name}
        </DialogTitle>
        <DialogContent>
          {selectedDraft && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Bundle Information</Typography>
              <Typography variant="body2"><strong>ID:</strong> {selectedDraft.id}</Typography>
              {selectedDraft.isOwner === false && (
                <Typography variant="body2">
                  <strong>Created by:</strong> {ownerLabel(selectedDraft)}{selectedDraft.owner?.email ? ` (${selectedDraft.owner.email})` : ''}
                </Typography>
              )}
              <Typography variant="body2"><strong>Quantity:</strong> {selectedDraft.quantity}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedDraft.status}</Typography>
              <Typography variant="body2"><strong>Bundle Storage:</strong> {selectedDraft.bundleShelfLocation || 'Not set'}</Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>Child SKU Picking Details</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', borderColor: '#ECECE4' }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#faf8f3' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Child SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Picked</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Shelf Locations</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selectedDraft.childSKUPicking).map(([skuId, picking]) => {
                      const childSKU = selectedDraft.bundleSKU.childSKUs.find(cs => cs.skuId === skuId);
                      return (
                        <TableRow key={skuId}>
                          <TableCell>
                            <Typography variant="subtitle2">{childSKU?.name || skuId}</Typography>
                          </TableCell>
                          <TableCell>{picking.totalRequired}</TableCell>
                          <TableCell>{picking.totalPicked}</TableCell>
                          <TableCell>
                            {picking.shelfPickings.map(sp => (
                              <Chip
                                key={sp.shelfLocation}
                                label={`${sp.shelfLocation}: ${sp.quantityToPick}`}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </TableCell>
                          <TableCell>
                            {picking.picked ? (
                              <CheckCircle color="success" />
                            ) : (
                              <Schedule color="warning" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}>Close</Button>
          {selectedDraft && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => {
                setViewDialogOpen(false);
                handleResumeDraft(selectedDraft);
              }}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.5 }}
            >
              Resume Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the draft for "{selectedDraft?.bundleSKU.name}"?
          </Typography>
          {selectedDraft?.isOwner === false && (
            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px' }}>
              This draft was created by <strong>{ownerLabel(selectedDraft)}</strong>. Deleting it removes it for them too.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Any picked inventory will remain reserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}>Cancel</Button>
          <Button
            onClick={confirmDeleteDraft}
            color="error"
            variant="contained"
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.5 }}
          >
            Delete Draft
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default DraftBundleDashboard;
